import { Gift } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { Raffle } from "@/campaigns/modules/raffle/types";
import {
  maskedWinnerName,
  prizeQuantity,
  prizeWinners,
  rafflePhase,
  totalPrizeCount,
} from "@/campaigns/modules/raffle/types";
import { PrizeGamesChips } from "@/components/campaigns/prize-games-chips";
import { PrizeText } from "@/components/prize-text";
import { Badge } from "@/components/ui/badge";
import { Card, CardTitle } from "@/components/ui/card";
import { useViewerHash } from "@/hooks/use-viewer-hash";
import { cn } from "@/lib/utils";
import { formatPrizeTitle } from "@/utils/helpers";
import type { PrizeSchema, RaffleWinnerSchema } from "@/types";

/** Winners shown by name before the rest collapse into "+N more". */
const NAMED_WINNERS = 4;

/** Who won a prize, masked names as chips with the reader first as "You" (gold). */
const PrizeWinners = ({ winners }: { winners: RaffleWinnerSchema[] }) => {
  const { t } = useTranslation();
  const viewerHash = useViewerHash();
  if (winners.length === 0) return null;
  const mine = winners.filter((w) => w.player === viewerHash);
  const others = winners.filter((w) => w.player !== viewerHash);
  const names = [...mine, ...others];
  const rest = names.length - NAMED_WINNERS;
  return (
    <div className="flex flex-wrap items-center gap-1.5 font-body text-[12px] leading-4 text-muted-foreground lg:text-[13px]">
      <span className="mr-0.5">{t("raffle.winnersHeading", "Winners")}</span>
      {names.slice(0, NAMED_WINNERS).map((w, i) =>
        w.player === viewerHash ? (
          <Badge key={i} variant="solid" color="prizes">
            {t("tournament.you", "You")}
          </Badge>
        ) : (
          <Badge key={i} variant="ghost" color="neutral">
            {maskedWinnerName(w.player_username)}
          </Badge>
        )
      )}
      {rest > 0 && (
        <Badge variant="ghost" color="prizes">
          {t("raffle.moreWinners", "+{{number}} more", {
            number: rest.toLocaleString(),
          })}
        </Badge>
      )}
    </div>
  );
};

/** One bracket of the prize ladder: how many of the prize are handed out, the prize, its games —
 *  and, once drawn, who won it. */
const RafflePrizeRow = ({
  prize,
  winners,
  drawn,
}: {
  prize: PrizeSchema;
  winners: RaffleWinnerSchema[];
  drawn: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <li className="glass-card glass-tile flex flex-col gap-2 rounded-(--border-radius-inner) px-2.5 py-2 lg:px-3">
      <div className="flex items-center gap-3">
        <Badge variant="ghost" color="neutral" className="shrink-0">
          {`${prizeQuantity(prize)}×`}
        </Badge>
        {/* The prize leads and is never truncated: it shrinks, then glides on its one row. Gold
            while it is still on offer, a plain light grey once the draw is over. */}
        <PrizeText
          size="inherit"
          weight="semibold"
          tone={drawn ? "flat" : "gold"}
          shadow={false}
          fit="marquee"
          className={cn(
            "min-w-0 flex-1 text-[14px] leading-5 lg:text-[15px]",
            drawn && "text-foreground-secondary"
          )}
        >
          {formatPrizeTitle(prize.title, t)}
        </PrizeText>
        <PrizeGamesChips games={prize.games ?? []} className="shrink-0" />
      </div>
      {drawn && <PrizeWinners winners={winners} />}
    </li>
  );
};

/**
 * "Prizes": what the draw hands out, a row per bracket with how many of that prize there are
 * (the bracket's width — 4 places on €20 Cash read "4×"). While the raffle is open a chip counts
 * every prize on offer and a line reminds that each ticket is a separate chance; once drawn both
 * go and the rows turn quiet and list their winners. No frame of its own — the overview card puts it under the draw
 * state, `RafflePrizesCard` wraps it for the sideways-phone pane.
 */
export const RafflePrizes = ({ raffle }: { raffle: Raffle }) => {
  const { t } = useTranslation();
  if (raffle.prizes.length === 0) return null;
  const drawn = rafflePhase(raffle) === "drawn";
  return (
    <section className="flex w-full flex-col gap-3 text-left">
      <CardTitle
        aside={
          !drawn && (
            <Badge variant="tonal" color="prizes" icon={<Gift />}>
              {t("raffle.prizesUpForGrabs", "{{count}} prizes up for grabs", {
                count: totalPrizeCount(raffle),
              })}
            </Badge>
          )
        }
      >
        {t("tournament.prizesHeading", "Prizes")}
      </CardTitle>
      <ul className="flex w-full flex-col gap-1.5">
        {raffle.prizes.map((prize, i) => (
          <RafflePrizeRow
            key={`${prize.place_from ?? "x"}-${prize.place_to ?? "x"}-${i}`}
            prize={prize}
            winners={prizeWinners(raffle, prize)}
            drawn={drawn}
          />
        ))}
      </ul>
      {/* A player wins at most once per bracket, so "more than one prize" is only true when there is
          more than one bracket. */}
      {!drawn && raffle.prizes.length > 1 && (
        <p className="font-body text-xs leading-4 text-muted-foreground lg:text-[13px] lg:leading-[18px]">
          {t(
            "raffle.everyTicketNewChance",
            "Every ticket is a new chance, so you can win more than one prize."
          )}
        </p>
      )}
    </section>
  );
};

export const RafflePrizesCard = ({
  raffle,
  className,
}: {
  raffle: Raffle;
  className?: string;
}) => {
  if (raffle.prizes.length === 0) return null;
  return (
    <Card className={className}>
      <RafflePrizes raffle={raffle} />
    </Card>
  );
};
